import { cloneElement, Component, useCallback, useEffect, useState } from "react";
import { Box, Button, Card, Divider, Grid, IconButton, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { GetPastTrips } from "../../context/trip/tripProvider";
import DraggableList from 'react-draggable-list';
import DragHandleIcon from '@mui/icons-material/DragHandle';

const SearchRegion = ({
    index,
    dragHandleProps,

    Autocomplete,
    updateWayPoint,
    mobileViewFieldSelect,
    list,
    removeSearchRegion,
    isLast,
    canShowCloseOption
}) => <Stack
    direction='row'
    alignItems='center'
    justifyContent='start'
>
    {/* Draggable Icon */}
    <Box {...dragHandleProps} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab' }}>
        <Tooltip title="Drag and reorder">
            <DragHandleIcon 
                sx={{
                    width: {xs: '16px', md: '24px'},
                    height: {xs: '16px', md: '24px'},
                    color: '#AFAFAF',
                    mr: '12px'
                }}
            />
        </Tooltip>
    </Box>

    {/* Circler bullet */}
    <Stack
        direction='column'
        sx={{
            transform: 'translateY(20px)',
            display: isLast ? 'none' : 'flex'
        }}
    >
        <PanoramaFishEyeIcon 
            sx={{
                pr: '12.5px'
            }}
        />

        <MoreVertIcon 
            sx={{
                py: 0.5
            }}
        />
    </Stack>

    {/* End Stop Icon */}
    <PlaceOutlinedIcon
        sx={{
            color: 'rgba(255, 0, 0, 1)',
            display: isLast ? 'flex' : 'none',
            pr: '12.5px'
        }}
    />

    {/* Search field */}
    <Box
        sx={{
            mr: '5px',
            width: '100%'
        }}
    >
        <AutoCompleteField 
            Autocomplete={Autocomplete}
            index={index}
            updateWayPoint={updateWayPoint}
            mobileViewFieldSelect={mobileViewFieldSelect}
            latitude={list.length > index ? list[index].coordinates.lat : -1}
            longitude={list.length > index ? list[index].coordinates.lng : -1}
        />
    </Box>

    {/* Delete */}
    <IconButton 
        size="small"
        aria-label="delete"
        onClick={() => removeSearchRegion(index)}
        className="onMouseOver"
        sx={{
            color: '#5E5959',
            display: canShowCloseOption ? 'flex' : 'none'
        }}
        disabled={!canShowCloseOption}
    >
        <HighlightOffIcon />
    </IconButton>
</Stack>

class DraggableItem extends Component {
  render() {
    const { item, dragHandleProps, commonProps } = this.props;
    const { fullList, Autocomplete, updateWayPoint, mobileViewFieldSelect, removeSearchRegion } = commonProps;

    // Find the current index of this item in the list
    const currentIndex = fullList.findIndex(i => i.id === item.id);
    const isLast = currentIndex === fullList.length - 1;

    const canShowCloseOption = fullList.length > 2;

    // Spread dragHandleProps to the element that should initiate the drag
    return (
        <div 
            style={{ 
                padding: '0px 25px', 
                border: 'none', 
                cursor: 'pointer'
            }}
        >
            <SearchRegion
                index={currentIndex}
                dragHandleProps={dragHandleProps}

                Autocomplete={Autocomplete}
                updateWayPoint={updateWayPoint}
                mobileViewFieldSelect={mobileViewFieldSelect}
                list={fullList}
                removeSearchRegion={removeSearchRegion}
                isLast={isLast}
                canShowCloseOption={canShowCloseOption}
            />
        </div>
    );
  }
}

const MapFilter = ({
    Autocomplete,
    calculateRoute,
    clearRoutes
}) => {
    const theme = useTheme();

    const [activeTourLoaded, setActiveTourLoaded] = useState(false);
    const [mobileViewFieldExpanded, setMobileViewFieldExpanded] = useState(false);

    const {activeTour} = GetPastTrips();

    const mobileViewFieldSelect = useCallback(() => {
        setMobileViewFieldExpanded(true);
    }, []);

    const [locationSearchFieldList, setLocationSearchFieldList] = useState([
        {
            id: 0,
            coordinates: {lat: -1, lng: -1}
        },
        {
            id: 1,
            coordinates: {lat: -1, lng: -1}
        }
    ]);

    const onLocationFieldRegionDragEnd = useCallback((newList) => {
        console.log(newList);
        setLocationSearchFieldList(newList); // Update the state with the new order
    }, []);

    const addNewSearchRegion = useCallback(() => {
        const newID = Date.now();

        setLocationSearchFieldList([
            ...locationSearchFieldList, 
            {
                id: newID,
                coordinates: {lat: -1, lng: -1}
            }
        ]);
    }, [locationSearchFieldList]);

    const removeSearchRegion = useCallback((index) => {
        const array = [...locationSearchFieldList];
        array.splice(index, 1);

        setLocationSearchFieldList(array);
    }, [locationSearchFieldList]);

    const updateWayPoint = useCallback((index, coordinates) => {
        setLocationSearchFieldList(prevSearchRegions => {
            const updated = [...prevSearchRegions];
            updated[index].coordinates = coordinates;
            return updated;
        });
    }, []);

    useEffect(() => {
        const coordinatesList = [];

        for(let i = 0; i < locationSearchFieldList.length; i++){
            coordinatesList.push(locationSearchFieldList[i].coordinates);
        }

        calculateRoute(coordinatesList);
        // setActiveTour(wayPointsField);
    }, [locationSearchFieldList]);

    useEffect(() => {
        if(!activeTourLoaded){
            console.log(activeTour);

            const list = [];

            for(let i = 0; i < activeTour.length; i++){
                list.push({
                    id: i,
                    coordinates: activeTour[i]
                });
            }

            setActiveTourLoaded(true);
            setLocationSearchFieldList(list);
        }
    }, [activeTour, activeTourLoaded]);

    return (
        <>
            <Card
                elevation={0}
                sx={{
                    display: 'inline-block',
                    width: {xs: '100vw', lg: 'fit-content'},
                    minWidth: {xs: '100vw', lg: '500px'},
                    borderRadius: {xs: '0px', lg: '8px'},
                    height: {xs: mobileViewFieldExpanded ? '90vh' : '200px', md: mobileViewFieldExpanded ? '100vh' : '225px', lg: 'fit-content'},
                    maxHeight: {xs: '100vh', lg: '50vh'},
                    zIndex: 1,
                    overflowY: 'scroll'
                }}
            >
                <Box
                    mt={{xs: 2, md: 4}}
                >
                    <DraggableList
                        list={locationSearchFieldList}
                        itemKey="id" // The unique key property name for each item
                        template={DraggableItem}
                        onMoveEnd={onLocationFieldRegionDragEnd}
                        container={() => document.body} // Reference to the scrollable container (document.body if none)
                        commonProps={{ 
                            fullList: locationSearchFieldList,
                            Autocomplete: Autocomplete,
                            updateWayPoint: updateWayPoint,
                            mobileViewFieldSelect: mobileViewFieldSelect,
                            removeSearchRegion: removeSearchRegion
                        }}
                    />
                </Box>

                <Stack
                    direction='column'
                    sx={{
                        my: {xs: '10px', md: '15px'}
                    }}
                >
                    <Divider 
                        sx={{
                            width: '100%',
                            mb: {xs: '10px', md: '15px'}
                        }}
                    />

                    <Stack
                        direction= 'row'
                        justifyContent='space-between'
                        alignItems='center'
                        width='100%'
                    >
                        {/* Add New Stop */}
                        <Button 
                            variant="text" 
                            startIcon={<ControlPointIcon sx={{color: "rgba(0, 0, 0, 1)"}}/>}
                            onClick={addNewSearchRegion}
                            className="onMouseOver"
                            sx={{
                                bgcolor: 'transparent',
                                color: 'rgba(94, 89, 89, 1)',
                                textTransform: 'none',
                                cursor: 'pointer',
                                p: 0,
                                ml: 4
                            }}
                            disableRipple
                        >
                            Add stop
                        </Button>

                        {/* Done */}
                        <Button 
                            variant="text" 
                            onClick={() => setMobileViewFieldExpanded(false)}
                            className="onMouseOver"
                            sx={{
                                bgcolor: 'transparent',
                                color: theme.palette.primary.main,
                                textTransform: 'none',
                                cursor: 'pointer',
                                p: 0,
                                mr: 2,
                                display: {xs: mobileViewFieldExpanded ? 'block' : 'none', lg: 'none'}
                            }}
                            disableRipple
                        >
                            Done
                        </Button>
                    </Stack>
                </Stack>
            </Card>
        </>
    );
}

export default MapFilter;

export const AutoCompleteField = ({
    Autocomplete,
    index,
    updateWayPoint,
    mobileViewFieldSelect,
    latitude,
    longitude
}) => {
    const [fieldValue, setFieldValue] = useState("");

    const [autoCompleteInstance, setAutoCompleteInstance] = useState(null);

    const onLoadField = useCallback((autocompleteInstance) => {
        setAutoCompleteInstance(autocompleteInstance);
        console.log('Autocomplete Instance loaded for child ' + index);
    }, []);

    const onPlaceChanged = useCallback(() => {
        if (autoCompleteInstance !== null) {
            console.log('Autocomplete deleted a location for child ' + index);

            const place = autoCompleteInstance.getPlace();

            setFieldValue(place.name);
            updateWayPoint(index, {lat: place.geometry.location.lat(), lng: place.geometry.location.lng()});
        } else {
            console.log('Autocomplete is not loaded yet for child ' + index);
        }
    }, [autoCompleteInstance]);

    const listenLocationName = useCallback((event) => {
        const val = event.target.value;

        setFieldValue(val);

        if(val.trim().length === 0){
            updateWayPoint(index, {lat: -1, lng: -1});
        }
    }, []);

    const getShort = (components, type) => components?.find(c => c.types.includes(type))?.short_name || null;

    useEffect(() => {
        if (fieldValue !== "" || latitude === -1 || longitude === -1) return;

        // eslint-disable-next-line no-undef
        const geocoder = new google.maps.Geocoder();

        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            if (status === "OK" && results[0]) {
                const shortName = getShort(results[0].address_components, "locality");
                setFieldValue(shortName);
            } else {
                console.error("Geocoder failed:", status);
            }
        });
    }, [latitude, longitude, fieldValue]);

    return(
        <>
            <Autocomplete
                onLoad={onLoadField}
                onPlaceChanged={onPlaceChanged}
                restrictions={{country: ['lk']}} //https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
            >
                <TextField 
                    label="" 
                    variant="outlined" 
                    placeholder={index === 0 ? "Choose starting point" : "Choose stop"}
                    value={fieldValue}
                    onChange={listenLocationName}
                    onSelect={mobileViewFieldSelect}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: '8px',
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: '2px solid #AFAFAF'
                            },
                            "&.Mui-focused": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: 'rgba(0, 0, 0, 1)',
                                    borderWidth: "2px",
                                },
                            },
                            '&:hover fieldset': {
                                border: '2px solid #AFAFAF',
                            }
                        },
                    }}
                    fullWidth
                />
            </Autocomplete>
        </>
    );
}